<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="820px">
    <v-card class="profile-dialog-card entity-form-dialog payment-create-dialog">
      <div class="profile-dialog-header entity-form-dialog__header">
        <div class="entity-form-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">mdi-cash-plus</v-icon>
          </v-avatar>
          <div>
            <span>Payments</span>
            <h2>{{ Payment.donation ? "Apply permanent Sadaqah support" : "Receive payment" }}</h2>
            <p>{{ Payment.donation ? "Clear selected parent balances using permanent Sadaqah support." : "Record a payment and apply it to one or more open invoices." }}</p>
          </div>
        </div>
        <v-btn icon aria-label="Close receive payment dialog" @click.stop="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <v-container class="pa-0" grid-list-lg>
          <section class="entity-form-dialog__section">
            <div class="entity-form-dialog__section-heading">
              <span><v-icon size="19" color="primary">mdi-account-outline</v-icon></span>
              <div>
                <h3>Payment summary</h3>
                <p>Confirm the payer and current open balance.</p>
              </div>
            </div>
            <v-btn-toggle v-if="CustomerId" v-model="collectionMode" mandatory class="payment-create-dialog__mode" color="primary">
              <v-btn value="manual"><v-icon left size="18">mdi-cash-register</v-icon>Manual</v-btn>
              <v-btn value="saved" :disabled="Payment.donation || !merchantOptions.length"><v-icon left size="18">mdi-credit-card-check-outline</v-icon>Saved card</v-btn>
              <v-btn value="advance" :disabled="Payment.donation || advanceAvailable <= 0"><v-icon left size="18">mdi-wallet-outline</v-icon>Advance</v-btn>
            </v-btn-toggle>
            <div v-if="CustomerId && collectionMode === 'saved'" class="entity-form-dialog__summary mt-3">
              <v-icon color="primary" size="18">mdi-shield-check-outline</v-icon>
              <span>The selected card will be charged once for {{ total | currency }}, then that payment will be allocated to every selected invoice.</span>
            </div>
            <div v-if="CustomerId && collectionMode === 'advance'" class="entity-form-dialog__summary mt-3">
              <v-icon color="primary" size="18">mdi-wallet-outline</v-icon>
              <span>{{ advanceAvailable | currency }} advance tuition is available. No card will be charged.</span>
            </div>
            <v-layout row wrap justify-space-between>
              <v-flex xs6 v-if="CustomerId">
                <div>Customer ID</div>
                <div class="font-weight-medium title">
                  {{ Customer.localId }}
                </div>
              </v-flex>
              <v-flex xs6 v-else>
                <div>Customer ID</div>
                <div class="font-weight-medium title">
                  {{ Customer.id }}
                </div>
              </v-flex>
              <v-flex shrink>
                <div>Balance</div>
                <div class="font-weight-medium title">
                  {{ Customer.balance | currency }}
                </div>
              </v-flex>
              <v-flex xs8>
                <div>Customer Name</div>
                <div class="font-weight-medium title text-capitalize">
                  {{ Customer.name }}
                </div>
              </v-flex>
              <v-flex shrink>
                <div>Paying</div>
                <div class="font-weight-medium title">
                  {{ total | currency }}
                </div>
              </v-flex>
            </v-layout>
          </section>

          <section class="entity-form-dialog__section">
            <div class="entity-form-dialog__section-heading">
              <span><v-icon size="19" color="primary">mdi-receipt</v-icon></span>
              <div>
                <h3>{{ Payment.donation ? "Support details" : "Payment details" }}</h3>
                <p>{{ Payment.donation ? "Choose the date this permanent support was approved." : "Choose how and when the payment was received." }}</p>
              </div>
            </div>
            <v-layout row wrap justify-space-between>
              <v-flex xs12 sm4>
                <DateSelector label="Payment Date" v-model="Payment.date" />
              </v-flex>

              <v-flex xs12 sm4 v-if="collectionMode === 'manual' || !CustomerId">
                <v-select dense outlined :items="Payment.donation ? ['Permanent Sadaqah'] : paymentMethods" v-model="Payment.paymentMethod" label="Payment Method"
                  :disabled="Payment.donation"
                  item-text="name" hide-details return-object></v-select>
              </v-flex>

              <v-flex xs12 sm4>
                <v-text-field type="number" dense outlined class="right-input" hide-details label="Total Payment"
                  @focus="$event.target.select()" v-model="InvoiceTotal"></v-text-field>
              </v-flex>

              <!-- <v-flex xs12 sm3>
                
              </v-flex> -->
              <v-flex xs12>
                <v-text-field dense outlined v-model="Payment.note" label="Memo" hide-details></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 v-if="CustomerId && collectionMode === 'saved'">
                <v-select v-model="selectedMerchantId" :items="merchantOptions" item-text="label" item-value="id"
                  dense outlined hide-details label="Merchant account" @change="loadCards" />
              </v-flex>
              <v-flex xs12 sm6 v-if="CustomerId && collectionMode === 'saved' && selectedMerchant && selectedMerchant.provider === 'PAYCOMPASS'">
                <v-select v-model="selectedCardId" :items="cards" :loading="loadingCard" item-value="id"
                  dense outlined hide-details label="Saved card">
                  <template v-slot:item="{ item }"><span class="text-capitalize">{{ item.card_type || item.payment_method_type || 'Card' }} •••• {{ item.last4 }}</span></template>
                  <template v-slot:selection="{ item }"><span class="text-capitalize">{{ item.card_type || item.payment_method_type || 'Card' }} •••• {{ item.last4 }}</span></template>
                </v-select>
              </v-flex>

            </v-layout>
            <div class="text-center mt-3">
              <span>Balance: {{ balance | currency }}/ Total:
                {{ InvoiceTotal | currency }}/ Payment Receiving:
                {{ total | currency }}</span>
            </div>
          </section>

          <section class="entity-form-dialog__section">
            <div class="entity-form-dialog__section-heading">
              <span><v-icon size="19" color="primary">mdi-file-document-multiple-outline</v-icon></span>
              <div>
                <h3>Invoice allocation</h3>
                <p>Select which open invoices this payment should cover.</p>
              </div>
            </div>
            <v-card :color="color" :disabled="InvoiceTotal === 0" outlined v-if="Invoice"
              class="payment-create-dialog__allocation">
              <v-card-text>
                <v-layout row wrap align-center>
                <v-flex xs7>
                  <v-select clearable dense outlined :items="Invoice" hide-details label="Select Invoice"
                    item-text="localId" return-object v-model="selectedInvoice">
                    <template slot="item" slot-scope="{ item }">
                      <span>{{ item.localId }}</span>
                      <v-spacer></v-spacer>
                      <span>{{ item.collectibleDue | currency }}</span>
                    </template>
                    <template v-slot:append-item v-if="Invoice.length !== 0">
                      <v-divider class="mt-2"></v-divider>
                      <v-list-item @click="payAll">
                        <v-list-item-content>
                          <v-list-item-title>Receive All</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-icon>
                          {{ InvoiceTotal | currency }}
                        </v-list-item-icon>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-flex>
                <v-flex xs3>
                  <v-text-field type="number" dense outlined class="right-input" hide-details label="Payment"
                    @focus="$event.target.select()" v-model="amount"></v-text-field>
                </v-flex>
                <v-flex shrink>
                  <v-btn @click="add" color="success"> add </v-btn>
                </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>

          <v-list class="list-250">
            <template v-for="(row, index) in Payment.PaymentDetails">
              <v-list-item :key="row.id">
                <v-list-item-avatar>
                  <v-btn icon text color="red" @click="remove(index)"><v-icon>delete</v-icon></v-btn>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ row.localId }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <span class="title">{{ row.amount | currency }}</span>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index + 1 < Payment.PaymentDetails.length" :key="index">
              </v-divider>
            </template>
          </v-list>
          </section>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>

      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <div v-if="CustomerId" class="payment-create-dialog__permanent-support">
          <v-checkbox class="ma-0" v-model="Payment.donation" label="Permanent Sadaqah support" color="primary"
            hide-details />
          <small>No money is received from the parent. The selected balances become permanent Sadaqah support.</small>
        </div>
        <v-spacer></v-spacer>
        <v-btn text @click="close()">Cancel</v-btn>
        <v-btn color="primary" depressed :disabled="!canSubmit" :loading="loading"
          @click="submit()"><v-icon left small>mdi-content-save-outline</v-icon>{{ total | currency }} {{ Payment.donation ? "Apply support" : "Save" }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import invoiceService from "@/modules/Invoice/service";
import customerService from "@/modules/Customer/service";
import donorService from "@/modules/Donors/service.js";
import paymentService from "../service";
import advancePaymentService from "@/modules/AdvancePayment/service";
import payCompassService from "@/modules/PayCompass/service.js";
import paymentValidation from "@/modules/Invoice/Payment/paymentValidation.js";
import DateSelector from "@/components/DateSelector";

export default {
  name: "create-payment",
  props: {
    createDialog: {
      default: false,
    },
    CustomerId: {
      default: null,
    },
    DonorId: {
      default: null,
    },
  },
  components: {
    DateSelector,
  },
  data() {
    return {
      collectionMode: "manual",
      collectionKey: null,
      advanceAvailable: 0,
      InvoiceTotal: 0,
      loadingCard: false,
      selectedInvoice: null,
      Invoice: null,
      loading: false,
      dialog: false,
      amount: 0,
      Customer: {
        name: null,
        balance: 0,
        localId: null,
      },
      cards: [],
      selectedMerchantId: null,
      selectedCardId: null,
      paymentMethods: [
        'Stripe',
        'Mohid',
        "Cash",
        "Check",
        "Paypal",
        "Debit Card",
        "Credit Card",
        "Wire",
        "Quick Pay",
        "Cash App",
        "Exchange",
        "Zelle",
        "Other",
      ],
      Payment: {
        CustomerId: 0,
        date: this.$moment().toISOString(),
        paymentMethod: "Stripe",
        PaymentDetails: [],
        mohidId: null,
        stripeId: null,
        donation: false
      },
    };
  },
  computed: {
    merchantOptions() {
      return ((this.Customer && this.Customer.MerchantCustomers) || []).map(mapping => {
        const account = mapping.MerchantAccount || (mapping.DonationCategory && mapping.DonationCategory.MerchantAccount);
        const provider = String((account && account.provider) || "").toUpperCase();
        return {
          id: mapping.id,
          provider,
          mapping,
          label: `${provider === "PAYCOMPASS" ? "PayCompass" : provider === "STRIPE" ? "Stripe" : provider} · ${(mapping.DonationCategory && mapping.DonationCategory.name) || (account && account.name) || "Tuition merchant"}`,
        };
      }).filter(option => ["STRIPE", "PAYCOMPASS"].includes(option.provider));
    },
    selectedMerchant() {
      return this.merchantOptions.find(option => option.id === this.selectedMerchantId) || null;
    },
    canSubmit() {
      if (!this.Payment.PaymentDetails.length || !(this.total > 0)) return false;
      if (this.Payment.donation || !this.CustomerId || this.collectionMode === "manual") return true;
      return !paymentValidation.isMultiInvoicePaymentDisabled({
        total: this.total,
        allocationCount: this.Payment.PaymentDetails.length,
        mode: this.collectionMode,
        hasMerchant: Boolean(this.selectedMerchant),
        requiresCard: Boolean(this.selectedMerchant && this.selectedMerchant.provider === "PAYCOMPASS"),
        hasCard: Boolean(this.selectedCardId),
        advanceAvailable: this.advanceAvailable,
      });
    },
    color() {
      let value = "indigo lighten-5";
      if (this.balance === 0) {
        value = "green lighten-5";
      } else {
        if (this.balance < 0) {
          value = "red lighten-5";
        } else {
          value = "indigo lighten-5";
        }
      }

      return value;
    },
    total() {
      let value = 0;
      this.Payment.PaymentDetails.map((row) => {
        value += parseFloat(row.amount);
      });

      return value;
    },
    balance() {
      return this.InvoiceTotal - parseFloat(this.total);
    },
  },
  watch: {
    "Payment.donation"(enabled) {
      if (!this.CustomerId) return;
      if (enabled) {
        this.collectionMode = "manual";
        this.Payment.paymentMethod = "Permanent Sadaqah";
      } else if (this.Payment.paymentMethod === "Permanent Sadaqah") {
        this.Payment.paymentMethod = "Cash";
      }
      this.Payment.PaymentDetails = this.Payment.PaymentDetails.map((row) => ({
        ...row,
        donation: Boolean(enabled),
        metadata: this.permanentSupportMetadata(row.metadata, enabled),
      }));
    },
    selectedInvoice(val) {
      if (!val) return;
      if (this.balance > val.collectibleDue) {
        this.amount = val.collectibleDue;
      } else {
        if (this.balance <= 0) {
          this.amount = val.collectibleDue;
        } else {
          this.amount = this.balance;
        }
      }
    },

    createDialog(val) {
      this.dialog = val;
      if (val) {
        this.collectionKey = this.newCollectionKey();
        this.loadOpenInvoice();
        this.getCustomer();
      }
    },
  },
  methods: {
    permanentSupportMetadata(metadata, enabled) {
      const value = metadata && typeof metadata === "object" && !Array.isArray(metadata)
        ? { ...metadata }
        : {};
      if (enabled) {
        value.source = "PERMANENT_SADAQAH_ASSISTANCE";
      } else if (value.source === "PERMANENT_SADAQAH_ASSISTANCE") {
        delete value.source;
      }
      return Object.keys(value).length ? value : null;
    },
    normalizePermanentSupport() {
      if (!this.CustomerId || !this.Payment.donation) return;
      this.collectionMode = "manual";
      this.Payment.paymentMethod = "Permanent Sadaqah";
      this.Payment.PaymentDetails = this.Payment.PaymentDetails.map((row) => ({
        ...row,
        donation: true,
        metadata: this.permanentSupportMetadata(row.metadata, true),
      }));
    },
    newCollectionKey() {
      return `multi-invoice-${this.CustomerId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    },
    async loadCards() {
      this.cards = [];
      this.selectedCardId = null;
      if (!this.selectedMerchant || this.selectedMerchant.provider !== "PAYCOMPASS") return;
      this.loadingCard = true;
      try {
        const mapping = this.selectedMerchant.mapping;
        const response = await payCompassService.getAllCard(mapping.payCompassId, { donationCategoryId: mapping.DonationCategoryId });
        this.cards = response.data || [];
        if (this.cards.length) this.selectedCardId = this.cards[0].id;
      } finally {
        this.loadingCard = false;
      }
    },
    async payAll() {
      this.$swal({
        title: "Are you sure?",
        text: `You want to select all the invoices for ${this.$options.filters.currency(
          this.InvoiceTotal
        )}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then(async (result) => {
        if (result.value) {
          await Promise.all(
            this.Invoice.map((row) => {
              let x = row;
              x.amount = row.collectibleDue;
              x.InvoiceId = row.id;
              row.donation = this.Payment.donation || false
              this.Payment.PaymentDetails.push(row);
            })
          );
          this.Invoice = [];
          this.emptybar();
        }
      });
    },
    add() {
      if (this.amount === 0) return false;
      const index = this.Invoice.findIndex(
        (x) => x.id === this.selectedInvoice.id
      );
      let row = this.Invoice[index];

      if (this.amount > this.Invoice[index].collectibleDue) {
        this.$swal({
          title: "Exceeded!",
          text: `Payment can't be more then ${this.Invoice[index].collectibleDue}`,
          icon: "warning",
        });
      } else {
        row.amount = this.amount;
        row.InvoiceId = row.id;
        row.donation = this.Payment.donation
        this.Payment.PaymentDetails.push(row);
        this.Invoice.splice(index, 1);
        this.amount = 0;
        // this.selectedInvoice = null
      }
    },
    emptybar() {
      this.amount = 0;
      this.selectedInvoice = null;
    },
    remove(i) {
      this.Invoice.push(this.Payment.PaymentDetails[i]);
      this.Payment.PaymentDetails.splice(i, 1);
    },
    async getCustomer() {

      if (this.CustomerId) {
        this.Customer = (await customerService.getById(this.CustomerId)).data
        const advanceSummary = await advancePaymentService.getSummary(this.CustomerId)
        this.advanceAvailable = Number(advanceSummary.data && advanceSummary.data.available || 0)
      } else {
        this.Customer = (await donorService.getById(this.DonorId)).data
      }

      console.log('this.Customer ', this.CustomerId, this.DonorId, this.Customer)

      if (!this.Customer) {
        console.log('customer not found')
        return false
      }

      if (this.CustomerId) {
        this.Payment.CustomerId = this.Customer.id;
        if (this.merchantOptions.length === 1) {
          this.selectedMerchantId = this.merchantOptions[0].id;
          await this.loadCards();
        }
      } else {
        this.Payment.DonorId = this.Customer.id;
      }

      if (this.Customer?.mohidId) {
          this.Payment.mohidId = this.Customer.mohidId
          this.Payment.paymentMethod = 'Mohid'
        }
        if (this.Customer?.stripeId) {
          this.Payment.stripeId = this.Customer.stripeId
          this.Payment.paymentMethod = 'Stripe'
        }

      // return customerService.getById(id).then((response) => {
      //   console.log('response', response)
      //   this.Customer = response.data;

        
      //   // this.Payment.CustomerId = this.Customer.id;

        
      //   return response;
      // });

    },
    loadOpenInvoice() {
      this.loading = true;
      let where = {
        due: true,
      }
      if (this.CustomerId) {
        where.CustomerId = this.CustomerId
      } else {
        where.DonorId = this.DonorId
      }
      console.log('where', where)
      return invoiceService
        .getAll(where)
        .then((response) => {
          this.loading = false;
          this.Invoice = response.data.map(row => ({
            ...row,
            collectibleDue: paymentValidation.collectibleInvoiceDue({
              isCustomer: Boolean(this.CustomerId),
              totalDue: row.due,
              parentDue: row.parentDue,
            }),
          })).filter(row => row.collectibleDue > 0);
          this.InvoiceTotal = 0
          this.Invoice.map((row) => {
            this.InvoiceTotal += row.collectibleDue;
          });
          return response;
        });
    },
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.InvoiceTotal = 0;
      this.collectionMode = "manual";
      this.collectionKey = this.newCollectionKey();
      this.advanceAvailable = 0;
      this.cards = [];
      this.selectedMerchantId = null;
      this.selectedCardId = null;
      this.Payment = {
        CustomerId: 0,
        date: this.$moment().toISOString(),
        paymentMethod: "Cash",
        PaymentDetails: [],
        mohidId: null,
        stripeId: null,
        donation: false
      };
    },
    async submit() {
      this.normalizePermanentSupport();
      if (!this.canSubmit) return;
      const isPermanentSupport = Boolean(this.CustomerId && this.Payment.donation);
      const confirmation = await this.$swal({
        title: isPermanentSupport ? "Apply permanent Sadaqah support" : this.collectionMode === "saved" ? "Charge saved card once?" : "Receive payment?",
        text: isPermanentSupport
          ? `${this.$options.filters.currency(this.total)} will clear the selected parent balance without recording parent cash. The support will appear in Permanent Support for the selected payment date.`
          : this.collectionMode === "saved"
            ? `${this.$options.filters.currency(this.total)} will be charged once and applied to ${this.Payment.PaymentDetails.length} selected invoice(s).`
            : this.collectionMode === "advance"
              ? `${this.$options.filters.currency(this.total)} of advance tuition will be applied to ${this.Payment.PaymentDetails.length} selected invoice(s).`
              : `You are receiving ${this.$options.filters.currency(this.total)}.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#43A047",
        cancelButtonColor: "#43A047",
        confirmButtonText: isPermanentSupport ? "Apply Support" : this.collectionMode === "saved" ? "Charge once" : "Receive payment",
        cancelButtonText: "No",
      });
      if (!confirmation.value) return;

      this.loading = true;
      try {
        if (this.collectionMode === "saved" && !isPermanentSupport) {
          await paymentService.collectInvoices({
            ...this.Payment,
            MerchantCustomerId: this.selectedMerchantId,
            paymentMethodId: this.selectedCardId,
            collectionKey: this.collectionKey,
          });
        } else if (this.collectionMode === "advance" && !isPermanentSupport) {
          await advancePaymentService.applyToInvoices({
            CustomerId: this.CustomerId,
            date: this.Payment.date,
            note: this.Payment.note,
            PaymentDetails: this.Payment.PaymentDetails,
            applicationKey: this.collectionKey,
          });
        } else {
          await paymentService.create(this.Payment);
        }
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 }).fire({
          icon: "success",
          title: this.collectionMode === "saved" ? "Card charged once and invoices paid" : "Payment was applied",
        });
        this.$emit("submit");
        this.$events.emit("paymentListEvent");
        this.$events.emit("customerProfileEvent");
        this.$events.emit("invoiceListEvent");
        this.$events.emit("advancePaymentListEvent");
        this.close();
      } catch (error) {
        if (this.collectionMode === "saved" && error.data && error.data.code !== "100005") {
          this.collectionKey = this.newCollectionKey();
        }
        this.$swal({
          icon: "error",
          title: "Payment was not completed",
          text: (error.data && error.data.message) || "Please review the payment details and try again.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.payment-create-dialog__allocation {
  overflow: hidden;
}

.payment-create-dialog__mode {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
}

.payment-create-dialog__mode .v-btn {
  height: 48px;
  text-transform: none;
  letter-spacing: 0;
}

.payment-create-dialog__permanent-support {
  max-width: 430px;
}

.payment-create-dialog__permanent-support small {
  display: block;
  color: #65758b;
  line-height: 1.35;
  margin-left: 32px;
}

@media (max-width: 600px) {
  .payment-create-dialog__mode { grid-template-columns: 1fr; }
}

</style>
